/**
 * Hospice Visit Template Builder
 * 
 * CMS CoP-compliant discipline-specific visit templates with:
 * - Guided prompts and survey-defensible phrasing
 * - Real-time compliance validation
 * - ADR risk mitigation notes
 * - Medicare CoP references
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import {
  Stethoscope,
  Heart,
  Users,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Info,
  Shield,
  Book,
  RefreshCw,
  Save,
  Send,
  Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const DISCIPLINE_ICONS = {
  rn_case_manager: Stethoscope,
  hospice_aide: Heart,
  social_worker: Users,
  chaplain: Book,
};

const DISCIPLINE_COLORS = {
  rn_case_manager: "bg-blue-500/20 text-blue-300",
  hospice_aide: "bg-pink-500/20 text-pink-300",
  social_worker: "bg-purple-500/20 text-purple-300",
  chaplain: "bg-amber-500/20 text-amber-300",
};

export function HospiceVisitTemplateBuilder({ patientId, patientName, onClose }) {
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [complianceGuide, setComplianceGuide] = useState(null);
  const [showCompliancePanel, setShowCompliancePanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  useEffect(() => {
    fetchDisciplines();
  }, []);

  useEffect(() => {
    if (template) {
      calculateCompletion();
    }
  }, [formData, template]);

  const fetchDisciplines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hospice-templates/disciplines`);
      setDisciplines(response.data.disciplines.filter(d => d.has_template));
      setLoading(false);
    } catch (err) {
      toast.error("Failed to load templates");
      setLoading(false);
    }
  };

  const selectDiscipline = async (disciplineId) => {
    setLoading(true);
    setSelectedDiscipline(disciplineId);
    
    try {
      const [templateRes, complianceRes] = await Promise.all([
        axios.get(`${API_URL}/api/hospice-templates/template/${disciplineId}`),
        axios.get(`${API_URL}/api/hospice-templates/compliance-guide/${disciplineId}`)
      ]);
      
      setTemplate(templateRes.data.template);
      setComplianceGuide(complianceRes.data);
      
      // Initialize expanded sections (expand all required sections)
      const expanded = {};
      templateRes.data.template.sections.forEach(section => {
        expanded[section.id] = !section.collapse_default;
      });
      setExpandedSections(expanded);
      
      // Initialize form data
      setFormData({});
    } catch (err) {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const calculateCompletion = () => {
    if (!template) return;
    
    let total = 0;
    let completed = 0;
    
    template.sections.forEach(section => {
      if (!section.required) return;
      section.fields.forEach(field => {
        if (field.required && !field.conditional_on) {
          total++;
          if (formData[field.id] !== undefined && formData[field.id] !== "") {
            completed++;
          }
        }
      });
    });
    
    setCompletionPercentage(total > 0 ? Math.round((completed / total) * 100) : 0);
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckboxGroupChange = (fieldId, option, checked) => {
    const current = formData[fieldId] || [];
    const updated = checked 
      ? [...current, option]
      : current.filter(o => o !== option);
    setFormData(prev => ({ ...prev, [fieldId]: updated }));
  };

  const validateForm = () => {
    const errors = [];
    template.sections.forEach(section => {
      if (!section.required) return;
      section.fields.forEach(field => {
        if (field.required) {
          // Check conditional
          if (field.conditional_on) {
            const condKey = Object.keys(field.conditional_on)[0];
            const condVal = field.conditional_on[condKey];
            if (formData[condKey] !== condVal) return;
          }
          
          if (!formData[field.id] || (Array.isArray(formData[field.id]) && formData[field.id].length === 0)) {
            errors.push({ section: section.title, field: field.label });
          }
        }
      });
    });
    return errors;
  };

  const saveVisit = async (isDraft = false) => {
    if (!isDraft) {
      const errors = validateForm();
      if (errors.length > 0) {
        setValidationErrors(errors);
        toast.error(`${errors.length} required fields missing`);
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/hospice-templates/submit`, {
        patient_id: patientId,
        discipline: selectedDiscipline,
        visit_type: formData.visit_type || "routine",
        template_id: template.id,
        data: formData,
        is_draft: isDraft
      });
      
      if (response.data.success) {
        toast.success(isDraft ? "Draft saved" : "Visit note submitted", {
          description: `Visit ID: ${response.data.visit_id}`
        });
        if (!isDraft) {
          onClose?.();
        }
      } else {
        toast.warning("Saved as draft", {
          description: `${response.data.validation_errors?.length || 0} validation issues`
        });
      }
    } catch (err) {
      toast.error("Failed to save visit note");
    } finally {
      setSaving(false);
    }
  };

  const isFieldVisible = (field) => {
    if (!field.conditional_on) return true;
    const condKey = Object.keys(field.conditional_on)[0];
    const condVal = field.conditional_on[condKey];
    return formData[condKey] === condVal || (Array.isArray(formData[condKey]) && formData[condKey].includes(condVal));
  };

  const renderField = (field) => {
    if (!isFieldVisible(field)) return null;
    
    const hasError = validationErrors.some(e => e.field === field.label);
    
    return (
      <div key={field.id} className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Label className={`text-sm ${field.required ? "font-medium" : ""} ${hasError ? "text-red-400" : ""}`}>
            {field.label}
            {field.required && <span className="text-red-400 ml-1">*</span>}
          </Label>
          {field.compliance_rationale && (
            <button
              onClick={() => setShowCompliancePanel(true)}
              className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
            >
              <Shield className="w-3 h-3" />
              CoP
            </button>
          )}
        </div>
        
        {field.help_text && (
          <p className="text-xs text-slate-500">{field.help_text}</p>
        )}
        
        {field.field_type === "text" && (
          <Input
            value={formData[field.id] || ""}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className={hasError ? "border-red-500" : ""}
          />
        )}
        
        {field.field_type === "textarea" && (
          <div className="space-y-1">
            <Textarea
              value={formData[field.id] || ""}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              className={`min-h-[100px] ${hasError ? "border-red-500" : ""}`}
            />
            {field.survey_defensible_phrases && (
              <div className="mt-2">
                <button
                  onClick={() => {
                    const phrase = field.survey_defensible_phrases[0];
                    handleFieldChange(field.id, (formData[field.id] || "") + " " + phrase);
                  }}
                  className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                >
                  <Lightbulb className="w-3 h-3" />
                  Suggested phrasing
                </button>
              </div>
            )}
          </div>
        )}
        
        {field.field_type === "select" && (
          <Select
            value={formData[field.id] || ""}
            onValueChange={(value) => handleFieldChange(field.id, value)}
          >
            <SelectTrigger className={hasError ? "border-red-500" : ""}>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {field.field_type === "radio" && (
          <RadioGroup
            value={formData[field.id] || ""}
            onValueChange={(value) => handleFieldChange(field.id, value)}
            className="flex flex-wrap gap-4"
          >
            {field.options?.map(opt => (
              <div key={opt} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`${field.id}-${opt}`} />
                <Label htmlFor={`${field.id}-${opt}`} className="text-sm font-normal">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        )}
        
        {field.field_type === "checkbox" && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={formData[field.id] || false}
              onCheckedChange={(checked) => handleFieldChange(field.id, checked)}
            />
            <Label htmlFor={field.id} className="text-sm font-normal">{field.help_text || "Confirmed"}</Label>
          </div>
        )}
        
        {(field.field_type === "checkbox_group" || field.field_type === "multiselect") && (
          <div className="grid grid-cols-2 gap-2">
            {field.options?.map(opt => (
              <div key={opt} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${opt}`}
                  checked={(formData[field.id] || []).includes(opt)}
                  onCheckedChange={(checked) => handleCheckboxGroupChange(field.id, opt, checked)}
                />
                <Label htmlFor={`${field.id}-${opt}`} className="text-sm font-normal">{opt}</Label>
              </div>
            ))}
          </div>
        )}
        
        {field.field_type === "number" && (
          <Input
            type="number"
            value={formData[field.id] || ""}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={`w-32 ${hasError ? "border-red-500" : ""}`}
          />
        )}
        
        {field.field_type === "date" && (
          <Input
            type="date"
            value={formData[field.id] || ""}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={hasError ? "border-red-500" : ""}
          />
        )}
        
        {field.field_type === "time" && (
          <Input
            type="time"
            value={formData[field.id] || ""}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={`w-32 ${hasError ? "border-red-500" : ""}`}
          />
        )}
        
        {field.field_type === "pain_scale" && (
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="10"
              value={formData[field.id] || 0}
              onChange={(e) => handleFieldChange(field.id, parseInt(e.target.value))}
              className="flex-1"
            />
            <Badge className={`w-12 justify-center ${
              (formData[field.id] || 0) <= 3 ? "bg-green-500/20 text-green-300" :
              (formData[field.id] || 0) <= 6 ? "bg-amber-500/20 text-amber-300" :
              "bg-red-500/20 text-red-300"
            }`}>
              {formData[field.id] || 0}/10
            </Badge>
          </div>
        )}
        
        {field.adr_risk_note && (
          <div className="flex items-start gap-1 mt-1">
            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-400">{field.adr_risk_note}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading && !selectedDiscipline) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading templates...</span>
      </div>
    );
  }

  // Discipline selection screen
  if (!selectedDiscipline) {
    return (
      <div className="space-y-4 p-4">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold">Select Discipline</h2>
          <p className="text-sm text-slate-400">Choose the discipline for this visit note</p>
          {patientName && (
            <Badge className="mt-2 bg-teal-500/20 text-teal-300">Patient: {patientName}</Badge>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {disciplines.map(disc => {
            const Icon = DISCIPLINE_ICONS[disc.id] || FileText;
            const color = DISCIPLINE_COLORS[disc.id] || "bg-slate-500/20 text-slate-300";
            
            return (
              <Card
                key={disc.id}
                className="bg-slate-800/50 border-slate-700 hover:border-teal-500/50 cursor-pointer transition-all"
                onClick={() => selectDiscipline(disc.id)}
                data-testid={`discipline-${disc.id}`}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <p className="font-medium">{disc.name}</p>
                  <p className="text-xs text-slate-500">CMS CoP Compliant</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Loading template
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading template...</span>
      </div>
    );
  }

  // Template form
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setSelectedDiscipline(null)}>
              ← Back
            </Button>
            <div>
              <h2 className="font-semibold">{template?.name}</h2>
              <p className="text-xs text-slate-400">{patientName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompliancePanel(true)}
            >
              <Shield className="w-4 h-4 mr-1" />
              Compliance
            </Button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <Progress value={completionPercentage} className="flex-1 h-2" />
          <Badge className={completionPercentage === 100 ? "bg-green-500/20 text-green-300" : "bg-slate-500/20 text-slate-300"}>
            {completionPercentage}%
          </Badge>
        </div>
        
        {validationErrors.length > 0 && (
          <Alert className="mt-3 bg-red-500/10 border-red-500/30">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <AlertTitle className="text-red-400">Missing Required Fields</AlertTitle>
            <AlertDescription className="text-red-300 text-xs">
              {validationErrors.slice(0, 3).map(e => `${e.section}: ${e.field}`).join(", ")}
              {validationErrors.length > 3 && ` and ${validationErrors.length - 3} more...`}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Form content */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {template?.sections.map(section => (
            <Collapsible
              key={section.id}
              open={expandedSections[section.id]}
              onOpenChange={(open) => setExpandedSections(prev => ({ ...prev, [section.id]: open }))}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-700/30 transition-colors py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {expandedSections[section.id] ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <CardTitle className="text-sm">{section.title}</CardTitle>
                        {section.required && (
                          <Badge className="text-xs bg-teal-500/20 text-teal-300">Required</Badge>
                        )}
                      </div>
                      {section.compliance_rationale && (
                        <Shield className="w-4 h-4 text-teal-400" />
                      )}
                    </div>
                    {section.description && (
                      <CardDescription className="text-xs ml-6">{section.description}</CardDescription>
                    )}
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    {section.fields.map(field => renderField(field))}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>

      {/* Footer actions */}
      <div className="p-4 border-t border-slate-700 flex justify-between">
        <Button variant="outline" onClick={() => saveVisit(true)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          Save Draft
        </Button>
        <Button onClick={() => saveVisit(false)} disabled={saving || completionPercentage < 100}>
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Visit Note
        </Button>
      </div>

      {/* Compliance Guide Panel */}
      <Sheet open={showCompliancePanel} onOpenChange={setShowCompliancePanel}>
        <SheetContent side="right" className="w-[400px] bg-slate-900 border-slate-700">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-white">
              <Shield className="w-5 h-5 text-teal-400" />
              Compliance Guide
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Medicare CoP requirements and ADR risk mitigation
            </SheetDescription>
          </SheetHeader>
          
          {complianceGuide && (
            <ScrollArea className="h-[calc(100vh-150px)] mt-4">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-teal-400 mb-2">Summary</h4>
                  <p className="text-sm text-slate-300">{complianceGuide.compliance_summary}</p>
                </div>
                
                <Separator className="bg-slate-700" />
                
                <div>
                  <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    ADR Risk Mitigation
                  </h4>
                  <ul className="space-y-2">
                    {complianceGuide.adr_mitigation_notes?.map((note, idx) => (
                      <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Separator className="bg-slate-700" />
                
                <div>
                  <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Book className="w-4 h-4" />
                    Medicare CoP References
                  </h4>
                  <ul className="space-y-1">
                    {complianceGuide.medicare_cop_references?.map((ref, idx) => (
                      <li key={idx} className="text-xs text-slate-400">{ref}</li>
                    ))}
                  </ul>
                </div>
                
                <Separator className="bg-slate-700" />
                
                <div>
                  <h4 className="text-sm font-medium text-purple-400 mb-2">Field-Level Compliance</h4>
                  <div className="space-y-2">
                    {complianceGuide.field_level_compliance?.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="text-xs bg-slate-800 p-2 rounded">
                        <p className="text-slate-400">{item.section} {item.field ? `→ ${item.field}` : ""}</p>
                        <p className="text-slate-300 mt-1">{item.rationale || item.adr_note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default HospiceVisitTemplateBuilder;
